/**
 * Utility functions for colors
 */

/**
 * Generates a deterministic color from a string (tag name)
 * This ensures the same string always gets the same color
 *
 * @param tagName The string to generate a color from
 * @returns A hexadecimal color string (e.g., "#ff0000")
 */
export function getTagColor(tagName: string): string {
  // Simple hash function to create a deterministic number from the string
  let hash = 0
  for (let i = 0; i < tagName.length; i++) {
    hash = tagName.charCodeAt(i) + ((hash << 5) - hash)
  }

  // Convert to hex color
  let color = "#"
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff
    color += ("00" + value.toString(16)).substr(-2)
  }

  return color
}

/**
 * Calculates the relative luminance of a color
 * Based on WCAG 2.0 formula
 *
 * @param hexColor The hexadecimal color string (e.g., "#ff0000")
 * @returns A value between 0 (black) and 1 (white)
 */
function getLuminance(hexColor: string): number {
  // Remove the # if present
  const hex = hexColor.charAt(0) === "#" ? hexColor.substring(1) : hexColor

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  // Calculate luminance
  const R = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
  const G = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
  const B = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

  return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

/**
 * Determines whether white or black text should be used for better contrast
 * Based on WCAG 2.0 guidelines for readability
 *
 * @param backgroundColor The hexadecimal color string (e.g., "#ff0000")
 * @returns "#ffffff" for white or "#000000" for black
 */
export function getContrastColor(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor)
  return luminance > 0.5 ? "#000000" : "#ffffff"
}

/**
 * Returns an object with background and text colors for a tag
 *
 * @param tagName The tag name to generate colors for
 * @returns Object with backgroundColor and textColor
 */
export function getTagColors(tagName: string): {
  backgroundColor: string
  textColor: string
} {
  const backgroundColor = getTagColor(tagName)
  const textColor = getContrastColor(backgroundColor)

  return {
    backgroundColor,
    textColor,
  }
}
